import type { QueryClient } from '@tanstack/react-query';

import BaseApiResponseError from '../api/http/BaseApiResponseError';
import UnprocessableEntity from '../api/http/UnprocessableEntity';
import { isErrorToastSuppressed } from './authErrorToast';

type SettledMutation = Parameters<typeof isErrorToastSuppressed>[0];

/**
 * Whether the user has ALREADY been told about `error`, given the mutation that
 * settled with it.
 *
 * An API refusal that came back through a React Query mutation was answered the
 * moment the mutation settled: `AuthProvider`'s mutation-cache subscription
 * toasts it (unless the mutation opted out with `meta.suppressErrorToast`
 * because it shows its own error UI), and a 401 there ends the session.
 *
 * A 422 is the exception: the subscription leaves it to a form's field errors,
 * so it was shown only if the mutation opted out of the toast because it
 * renders its own error UI.
 *
 * Anything that is not a `BaseApiResponseError` — a mapper that threw, a plain
 * `Error` — reached no toast, and must stay visible to Sentry.
 */
export function isAnswerOf(
  error: unknown,
  mutation: SettledMutation | undefined,
) {
  if (!mutation || !(error instanceof BaseApiResponseError)) return false;
  if (error instanceof UnprocessableEntity) {
    return isErrorToastSuppressed(mutation);
  }
  return true;
}

/**
 * Remembers every error a mutation of `queryClient` settled with, so a rejection
 * that nobody awaited can later be recognized as one the user already saw
 * (MYFITWORLD-V2-34 / V2-70).
 *
 * A subscription and not a cache lookup: `AuthProvider` answers a session-ending
 * error with `queryClient.clear()` in the same notification, and the mutation is
 * gone before the rejection reaches `unhandledrejection`. A `WeakMap`, so an
 * error nobody holds any more is not kept alive by being remembered.
 */
export function trackAnsweredMutationErrors(queryClient: QueryClient) {
  const settled = new WeakMap<object, SettledMutation>();
  const stop = queryClient.getMutationCache().subscribe((event) => {
    if (event.type !== 'updated' || event.action.type !== 'error') return;
    const { error } = event.action;
    if (typeof error === 'object' && error !== null) {
      settled.set(error, event.mutation);
    }
  });

  return {
    stop,
    wasAnswered(error: unknown) {
      if (typeof error !== 'object' || error === null) return false;
      return isAnswerOf(error, settled.get(error));
    },
  };
}
